import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { HitsterEvent, EventRound } from '../types';
import './PrintCards.css';

const PrintCards: React.FC = () => {
  const { eventId, roundId } = useParams<{ eventId: string; roundId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const teamId = searchParams.get('team');
  
  const [event, setEvent] = useState<HitsterEvent | null>(null);
  const [round, setRound] = useState<EventRound | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetch(`/api/events/${eventId}`);
        if (response.ok) {
          const data = await response.json();
          setEvent(data);
          const r = data.rounds.find((r: EventRound) => r.id === roundId);
          setRound(r || null);
        }
      } catch (err) {
        console.error('Failed to load event:', err);
      }
      setLoading(false);
    };
    loadData();
  }, [eventId, roundId]);

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate(`/round-scoring/${eventId}/${roundId}`);
  };

  if (loading) {
    return <div className="print-cards loading">Laden...</div>;
  }

  if (!event || !round) {
    return <div className="print-cards error">Ronde niet gevonden</div>;
  }

  // Determine which teams/years to print
  const mode = round.startYearMode || 'shared';
  const sharedYear = round.startYear || 1990;

  // If specific team requested, only show that team
  // If mode is 'shared', show one set for all teams
  // If mode is 'perTeam', show one set per team
  let cardSets: { teamName: string; startYear: number }[] = [];

  if (teamId) {
    // Specific team requested
    const team = event.teams?.find(t => t.id === teamId);
    if (team) {
      const teamRoundData = team.roundData?.find(rd => rd.roundId === roundId);
      const year = teamRoundData?.startYear || sharedYear;
      cardSets.push({ teamName: team.name, startYear: year });
    }
  } else if (mode === 'shared') {
    // One set for all teams with shared year
    cardSets.push({ teamName: 'Alle teams', startYear: sharedYear });
  } else {
    // Per team mode - one set per team
    (event.teams || []).forEach(team => {
      const teamRoundData = team.roundData?.find(rd => rd.roundId === roundId);
      const year = teamRoundData?.startYear || sharedYear;
      cardSets.push({ teamName: team.name, startYear: year });
    });
  }

  return (
    <div className="print-cards">
      {/* Controls - hidden when printing */}
      <div className="print-controls no-print">
        <button onClick={handleBack} className="print-cards-close-btn" title="Sluiten" aria-label="Sluiten">
          ✕
        </button>
        <h1>Kaartjes printen - {round.name}</h1>
        <button onClick={handlePrint} className="print-cards-print-btn">
          🖨️ Printen
        </button>
      </div>

      {/* Card sets */}
      {cardSets.map((cardSet, setIndex) => (
        <div key={setIndex} className="card-set">
          <div className="set-header no-print">
            <h2>{cardSet.teamName} - Startjaar: {cardSet.startYear}</h2>
          </div>
          
          <div className="cards-grid">
            {/* Start year card */}
            <div className="card start-card">
              <div className="card-content">
                <div className="card-label">START</div>
                <div className="card-year">{cardSet.startYear}</div>
                <div className="card-team">{cardSet.teamName}</div>
              </div>
            </div>

            {/* 10 blank cards */}
            {Array.from({ length: 10 }, (_, i) => (
              <div key={i} className="card blank-card">
                <div className="card-content">
                  <div className="card-number">{i + 1}</div>
                  <div className="card-field title-field">
                    <label>Titel:</label>
                  </div>
                  <div className="card-field artist-field">
                    <label>Artiest:</label>
                  </div>
                  <div className="card-field year-field">
                    <label>Jaar:</label>
                    <div className="write-box"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Page break between sets when printing */}
          {setIndex < cardSets.length - 1 && <div className="page-break"></div>}
        </div>
      ))}
    </div>
  );
};

export default PrintCards;
